import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { listAnneesScolaires, setCurrentAnneeId, type AnneeScolaire } from "@/lib/api";

// NOTE: useCurrentUser().user (type AuthMe) does NOT expose an ecoleId field.
// The backend scopes /api/annees-scolaires by the JWT claims directly (EcoleScopeChecker),
// so we call listAnneesScolaires() with no argument and let the JWT do the scoping.
// No `enabled` guard needed: the backend returns [] for unauthenticated calls
// (apiFetchOrEmpty fallback), which is safe.

const SS_KEY = "catho.school.anneeId";

interface AnneeScopeValue {
  anneeId: string | null;
  setAnneeId: (id: string) => void;
  annees: AnneeScolaire[];
  isLoading: boolean;
  /** L'année sélectionnée est-elle l'année active (est_active) ? Les écritures ne sont autorisées que sur l'année active. */
  isActiveAnnee: boolean;
}

const Ctx = createContext<AnneeScopeValue>({
  anneeId: null,
  setAnneeId: () => {},
  annees: [],
  isLoading: false,
  isActiveAnnee: false,
});

export function useAnneeScope() { return useContext(Ctx); }

/**
 * Fournit l'année scolaire de travail (back-office école/économat), persistante
 * en sessionStorage (URL propre, survit au F5 dans le même onglet). L'année est
 * envoyée au backend via le header X-Annee-Scolaire-Id (cf. apiFetch).
 *
 * Sélection automatique :
 *   1. estActive=true  (année en cours marquée explicitement)
 *   2. premier statut ≠ "CLOTUREE"  (A_VENIR ou EN_COURS)
 *   3. annees[0]  (toutes clôturées — afficher quand même la dernière)
 */
export function AnneeScolaireProvider({ children }: { children: ReactNode }) {
  const { data: annees = [], isLoading } = useQuery({
    queryKey: ["annees-scolaires"],
    queryFn: () => listAnneesScolaires(),
  });

  const [anneeId, setAnneeIdState] = useState<string | null>(() => {
    try { return sessionStorage.getItem(SS_KEY); } catch { return null; }
  });

  // Auto-sélection : si rien de valide en sessionStorage, choisir l'année active.
  useEffect(() => {
    if (annees.length === 0) return;
    const valid = anneeId && annees.some((a) => a.id === anneeId);
    if (!valid) {
      const active =
        annees.find((a) => a.estActive) ??
        annees.find((a) => a.statut !== "CLOTUREE") ??
        annees[0];
      setAnneeIdState(active.id);
      try { sessionStorage.setItem(SS_KEY, active.id); } catch { /* ignore */ }
    }
  }, [annees, anneeId]);

  // Garder apiFetch synchronisé avec l'année courante (montage + auto-select).
  useEffect(() => { setCurrentAnneeId(anneeId); }, [anneeId]);

  const setAnneeId = (id: string) => {
    setAnneeIdState(id);
    // SYNCHRONE : poser l'année AVANT que l'appelant n'invalide les requêtes
    // (AnneeSelector fait setAnneeId() puis invalidateQueries()). Sans ça, le
    // refetch partirait avec l'ancienne année (race identique à DioceseScopeProvider).
    setCurrentAnneeId(id);
    try { sessionStorage.setItem(SS_KEY, id); } catch { /* ignore */ }
  };

  const isActiveAnnee = annees.find((a) => a.id === anneeId)?.estActive ?? false;

  return (
    <Ctx.Provider value={{ anneeId, setAnneeId, annees, isLoading, isActiveAnnee }}>
      {children}
    </Ctx.Provider>
  );
}
